'use client';

import { c as _c } from "react/compiler-runtime";
import type { CodeContent } from '@/types/github';
import { useQueryClient } from '@tanstack/react-query';
import type { HTMLAttributes, ReactNode } from 'react';
import { createContext, memo, use, useMemo } from 'react';
import { hydrateOmittedCodeContents } from '@/data/v2/pr/hydrate-omitted-code-contents';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { useDiffNavigationStore } from '@/lib/stores/navigation/diff-navigation-store';
import { usePrStore } from '@/lib/stores/pr-store';
import { findMatchingFilename } from '@/lib/symbols/find-symbol-definition';
import { canNavigateSymbolReference, navigateSymbolReference } from '@/lib/symbols/navigate-symbol-reference';
import { cn } from '@/lib/utils';
type SymbolNavigationContextValue = {
  codeContent: CodeContent[];
  // Comparison SHAs of the code-changes payload behind `codeContent`. Click-time hydration must
  // use the same SHAs (and therefore the same query keys) as the chat request hydration, both to
  // fetch the correct file versions and to avoid refetching already-hydrated bodies under a
  // different key.
  headSha: string | null;
  baseSha: string | null;
  originElementId?: string;
  preferredFiles: CodeContent[];
};
const SymbolNavigationContext = createContext<SymbolNavigationContextValue>({
  codeContent: [],
  headSha: null,
  baseSha: null,
  preferredFiles: []
});
export function SymbolNavigationProvider(t0) {
  const $ = _c(9);
  const {
    codeContent,
    headSha,
    baseSha,
    children,
    originElementId,
    preferredFiles
  } = t0;
  const t1 = preferredFiles ?? codeContent;
  let t2;
  if ($[0] !== baseSha || $[1] !== codeContent || $[2] !== headSha || $[3] !== originElementId || $[4] !== t1) {
    t2 = {
      codeContent,
      headSha,
      baseSha,
      originElementId,
      preferredFiles: t1
    };
    $[0] = baseSha;
    $[1] = codeContent;
    $[2] = headSha;
    $[3] = originElementId;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const value = t2;
  let t3;
  if ($[6] !== children || $[7] !== value) {
    t3 = <SymbolNavigationContext value={value}>{children}</SymbolNavigationContext>;
    $[6] = children;
    $[7] = value;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function extractTextContent(node: ReactNode): string {
  if (typeof node === 'string') {
    return node;
  }
  if (typeof node === 'number') {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractTextContent).join('');
  }
  if (node && typeof node === 'object' && 'props' in node) {
    return extractTextContent((node as {
      props: {
        children?: ReactNode;
      };
    }).props.children);
  }
  return '';
}
export const ClickableInlineCode = memo(t0 => {
  const $ = _c(6);
  const {
    children,
    ...rest
  } = t0;
  const {
    codeContent,
    headSha,
    baseSha,
    originElementId,
    preferredFiles
  } = use(SymbolNavigationContext);
  const navigateToFile = useDiffNavigationStore(_temp);
  const queryClient = useQueryClient();
  const {
    owner,
    repo,
    number,
    installationId
  } = usePrStore();
  const symbolText = extractTextContent(children).trim();
  const isClickable = canNavigateSymbolReference(symbolText, codeContent, {
    allowFilenameMatch: true
  });
  const navigateWithHydratedContents = async () => {
    let allCodeContent = codeContent;
    let resolvedPreferredFiles = preferredFiles;
    const requiresContentSearch = !findMatchingFilename(symbolText, codeContent);
    if (requiresContentSearch && codeContent.some(_temp2)) {
      try {
        allCodeContent = await hydrateOmittedCodeContents(queryClient, owner, repo, number, codeContent, {
          headSha,
          baseSha
        });
        const hydratedByFilename = new Map(allCodeContent.map(_temp3));
        resolvedPreferredFiles = preferredFiles.map(file_0 => hydratedByFilename.get(file_0.filename) ?? file_0);
      } catch {
        allCodeContent = codeContent;
        resolvedPreferredFiles = preferredFiles;
      }
    }
    navigateSymbolReference({
      text: symbolText,
      allCodeContent,
      navigateToFile,
      preferredFiles: resolvedPreferredFiles,
      originElementId,
      allowFilenameMatch: true
    });
  };
  const handleClick = () => {
    trackClientEvent("v2_pr_navigated_to_symbol", installationId?.toString(), {
      source: "chat",
      symbol: symbolText,
      owner,
      repo,
      pr_number: number
    });
    navigateWithHydratedContents();
  };
  const t1 = isClickable ? "" : undefined;
  const t2 = isClickable ? handleClick : undefined;
  const t3 = cn(rest.className, isClickable && "cursor-pointer rounded bg-zinc-800 px-1 font-semibold hover:underline hover:decoration-zinc-500");
  let t4;
  if ($[0] !== children || $[1] !== rest || $[2] !== t1 || $[3] !== t2 || $[4] !== t3) {
    t4 = <code {...rest} data-clickable-symbol={t1} onClick={t2} className={t3}>{children}</code>;
    $[0] = children;
    $[1] = rest;
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
});
ClickableInlineCode.displayName = 'ClickableInlineCode';
function _temp(state) {
  return state.navigateToFile;
}
function _temp2(file_1) {
  return file_1.contentsOmitted === true;
}
function _temp3(file) {
  return [file.filename, file];
}